import { FilterValue } from 'antd/es/table/interface';
import { Expose, Transform, Type } from 'class-transformer';
import { decorate } from 'ts-mixer';

import type { ZonedDateTime } from '@/infra/adapters/datetime';
import datetime, { TimeUnit } from '@/infra/adapters/datetime';
import {
    DataOption as dataOption,
    GranularityType,
    ReportDataOption as reportDataOption,
} from '@/v1/domain/models';

export class DataList<T> {
    @decorate(Expose({ name: 'total' }))
    total: number = 0;
    @decorate(Expose({ name: 'rows' }))
    rows: T[] = [];
}

export class DataOption {
    @decorate(Expose({ name: 'offset' }))
    @decorate(
        Transform(({ value, obj }) => (value - 1) * obj.pageSize, {
            toPlainOnly: true,
        })
    )
    current: number = 1;
    @decorate(Expose({ name: 'limit' }))
    pageSize: number = 50;

    @decorate(Expose({ name: 'time_field' }))
    @decorate(
        Transform(({ value }) => value?.replace('[]', ''), {
            toPlainOnly: true,
        })
    )
    timeField?: string = 'upt_time';
    timeRange?: [ZonedDateTime, ZonedDateTime];
    @decorate(Expose({ name: 'start_time' }))
    @decorate(Type(() => Date))
    @decorate(
        Transform(
            ({ obj }) =>
                obj.timeRange?.[0]?.startOf(TimeUnit.DAY).toISOString(),
            {
                toPlainOnly: true,
            }
        )
    )
    startTime?: ZonedDateTime;
    @decorate(Expose({ name: 'end_time' }))
    @decorate(Type(() => Date))
    @decorate(
        Transform(
            ({ obj }) =>
                obj.timeRange?.[1]
                    .add(1, TimeUnit.DAY)
                    .startOf(TimeUnit.DAY)
                    .toISOString(),
            {
                toPlainOnly: true,
            }
        )
    )
    endTime?: ZonedDateTime;

    @decorate(Expose({ name: 'sort_field' }))
    @decorate(
        Transform(({ value }) => value?.replace('[]', ''), {
            toPlainOnly: true,
        })
    )
    sortField?: string = '';
    @decorate(Expose({ name: 'sort_order' }))
    @decorate(
        Transform(({ value }) => value?.replace('end', ''), {
            toPlainOnly: true,
        })
    )
    sortOrder?: string = 'ascend'; // asc, desc

    @decorate(Expose({ name: 'filters' }))
    @decorate(
        Transform(
            ({ value }) =>
                Object.fromEntries(
                    Object.entries(value)
                        .filter(
                            ([key, val]) => key.endsWith('[]') && val != null
                        )
                        .map(([key, val]) => [key.replace('[]', ''), val])
                ),
            {
                toPlainOnly: true,
            }
        )
    )
    filters: Record<string, FilterValue> = {};

    @decorate(Expose({ name: 'searchers' }))
    @decorate(
        Transform(
            ({ value, obj }) => ({
                ...value,
                ...Object.fromEntries(
                    Object.entries(obj.filters as Record<string, FilterValue>)
                        .filter(
                            ([key, val]) => !key.endsWith('[]') && val != null
                        )
                        .map(([key, val]) => [key, val[0]])
                ),
            }),
            {
                toPlainOnly: true,
            }
        )
    )
    searchers: Record<string, string> = {};
    @decorate(Expose({ name: 'monitor' }))
    monitor?: Record<string, string | number> = {};

    public constructor(obj?: dataOption) {
        if (!obj) return;
        this.current = obj.current;
        this.pageSize = obj.pageSize;
        this.timeField = obj.timeField;
        this.timeRange = obj.timeRange;
        this.sortField = obj.sortField;
        this.sortOrder = obj.sortOrder;
        this.filters = obj.filters;
        this.searchers = obj.searchers;
        this.monitor = obj.monitor;
    }
}

export class ReportDataOption {
    current: number = 1;
    pageSize: number = 50;
    timeRange?: [ZonedDateTime, ZonedDateTime] = [
        datetime.today().add(-1, TimeUnit.WEEK),
        datetime.today(),
    ];
    @decorate(Expose({ name: 'start_time' }))
    @decorate(Type(() => Date))
    @decorate(
        Transform(
            ({ obj }) =>
                obj.timeRange?.[0]?.startOf(TimeUnit.DAY).toISOString(),
            {
                toPlainOnly: true,
            }
        )
    )
    startTime?: ZonedDateTime;
    @decorate(Expose({ name: 'end_time' }))
    @decorate(Type(() => Date))
    @decorate(
        Transform(
            ({ obj }) =>
                obj.timeRange?.[1]
                    .add(1, TimeUnit.DAY)
                    .startOf(TimeUnit.DAY)
                    .toISOString(),
            {
                toPlainOnly: true,
            }
        )
    )
    endTime?: ZonedDateTime;

    @decorate(Expose({ name: 'granularity' }))
    granularity: GranularityType = GranularityType.ALL;

    @decorate(Expose({ name: 'fields' }))
    fields?: Array<string> = [];

    @decorate(Expose({ name: 'filters' }))
    @decorate(
        Transform(
            ({ value }) =>
                Object.fromEntries(
                    Object.entries(value)
                        .filter(
                            ([key, val]) => key.endsWith('[]') && val != null
                        )
                        .map(([key, val]) => [key.replace('[]', ''), val])
                ),
            {
                toPlainOnly: true,
            }
        )
    )
    filters: Record<string, FilterValue> = {};

    @decorate(Expose({ name: 'searchers' }))
    @decorate(
        Transform(
            ({ value, obj }) => ({
                ...value,
                ...Object.fromEntries(
                    Object.entries(obj.filters as Record<string, FilterValue>)
                        .filter(
                            ([key, val]) => !key.endsWith('[]') && val != null
                        )
                        .map(([key, val]) => [key, val[0]])
                ),
            }),
            {
                toPlainOnly: true,
            }
        )
    )
    searchers: Record<string, string> = {};

    public constructor(obj?: reportDataOption) {
        if (!obj) return;
        this.current = obj.current;
        this.pageSize = obj.pageSize;
        this.timeRange = obj.timeRange;
        this.granularity = obj.granularity;
        this.fields = obj.fields;
        this.filters = obj.filters;
        this.searchers = obj.searchers;
    }
}
